import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Box, Button, Typography, Grid, Card, Snackbar, Alert, CircularProgress } from '@mui/material';
import React from 'react';
import { FieldErrors, UseFormRegister } from 'react-hook-form';

import { FeedbackProps, FormValues } from './DocumentSigner.container';

interface DocumentSignerProps {
  onSubmit: () => void;
  register: UseFormRegister<FormValues>;
  errors: FieldErrors<FormValues>;
  feedback: FeedbackProps | null;
  onCloseFeedback: () => void;
  isAuthenticated: boolean;
  isSubmitting: boolean;
  isCheckingStatus: boolean;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDownload: () => void;
  onCheckStatus: () => void;
  signatureStatus?: string;
  fileId?: string;
  fileUrl: string | null;
  coordinates: { x: number; y: number } | null;
  onPdfClick: (x: number, y: number) => void;
}

export const DocumentSigner: React.FC<DocumentSignerProps> = ({
  onSubmit,
  register,
  errors,
  feedback,
  onCloseFeedback,
  isAuthenticated,
  isSubmitting,
  isCheckingStatus,
  onFileChange,
  onDownload,
  onCheckStatus,
  fileId,
  fileUrl,
  coordinates,
  onPdfClick,
  signatureStatus,
}) => {
  const [isSelecting, setIsSelecting] = React.useState(false);

  return (
    <Box>
      <Card sx={{ py: 2, px: 6 }}>
        <Grid container>
          <Grid item xs={12}>
            <Typography align="center" variant="h5" gutterBottom>
              Assinatura de Documento
            </Typography>
            <hr />

            {isAuthenticated ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  onSubmit();
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <input type="file" accept=".pdf" {...register('file')} onChange={onFileChange} />
                    {errors.file && <Typography color="error">{errors.file.message}</Typography>}
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth
                      disabled={isSubmitting}
                      startIcon={isSubmitting ? <CircularProgress size={24} /> : undefined}
                    >
                      Enviar para Assinatura
                    </Button>
                  </Grid>
                </Grid>
              </form>
            ) : (
              <Typography variant="subtitle1" align="center">
                Inicie a sessão para assinar um documento.
              </Typography>
            )}

            {fileUrl && (
              <Box sx={{ position: 'relative', width: '100%', height: '600px', mt: 4 }}>
                <iframe
                  src={fileUrl}
                  title="Visualizador de PDF"
                  style={{ width: '100%', height: '100%', border: '1px solid #ccc' }}
                />

                {isSelecting && (
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label="Selecione o local para a assinatura"
                    onClick={(event) => {
                      const rect = event.currentTarget.getBoundingClientRect();
                      onPdfClick(event.clientX - rect.left, event.clientY - rect.top);
                      setIsSelecting(false);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        const rect = event.currentTarget.getBoundingClientRect();
                        onPdfClick(rect.width / 2, rect.height / 2);
                        setIsSelecting(false);
                      }
                    }}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      cursor: 'crosshair',
                      backgroundColor: 'rgba(0, 0, 0, 0)',
                    }}
                  />
                )}

                {coordinates && (
                  <CheckCircleIcon
                    sx={{
                      position: 'absolute',
                      top: coordinates.y,
                      left: coordinates.x,
                      transform: 'translate(-50%, -50%)',
                      color: 'green',
                      fontSize: '32px',
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </Box>
            )}

            {fileUrl && (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    color={isSelecting ? 'error' : 'primary'}
                    fullWidth
                    onClick={() => setIsSelecting(!isSelecting)}
                    startIcon={<LocationOnIcon />}
                  >
                    {isSelecting ? 'Cancelar Seleção do Local' : 'Selecionar Local para Assinatura'}
                  </Button>
                </Grid>

                {fileId && (
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      color="primary"
                      fullWidth
                      onClick={onCheckStatus}
                      disabled={isCheckingStatus}
                      startIcon={isCheckingStatus ? <CircularProgress size={24} /> : undefined}
                    >
                      Verificar Status da Assinatura
                    </Button>
                  </Grid>
                )}

                {fileId && signatureStatus === 'APPROVED' && (
                  <Grid item xs={12}>
                    <Button variant="contained" color="secondary" fullWidth onClick={onDownload}>
                      Baixar Documento Assinado
                    </Button>
                  </Grid>
                )}
              </Grid>
            )}
          </Grid>
        </Grid>
      </Card>

      {feedback && (
        <Snackbar open={true} autoHideDuration={4000} onClose={onCloseFeedback}>
          <Alert onClose={onCloseFeedback} severity={feedback.type} sx={{ width: '100%' }}>
            {feedback.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};
